import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { tdStyle, thStyle } from "../../helpers/constants";
import { Chip, Dash, IconAction } from "../../helpers/general";

interface RowProps {
  inspector: Inspector;
  deletedList: React.Dispatch<BulkDeleteActions>;
  isDeleting: boolean;
  onDelete: () => void;
}

function InspectorRow({
  inspector,
  deletedList,
  isDeleting,
  onDelete,
}: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deletedList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: inspector.id,
      });
    },
    [deletedList, inspector.id],
  );

  // A row that unmounts (page change, search) must not stay selected
  useEffect(
    () => () => deletedList({ type: DELETE_ACTIONS.REMOVE, id: inspector.id }),
    [deletedList, inspector.id],
  );

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ minWidth: 220 }}
        >
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle fw-bold"
            style={{
              width: 36,
              height: 36,
              color: "#f0813f",
              backgroundColor: "rgba(240,129,63,0.14)",
            }}
          >
            {(inspector.fullName?.[0] ?? "?").toUpperCase()}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold text-capitalize">
              {inspector.fullName}
            </div>
            <div
              className="text-muted text-truncate"
              style={{ fontSize: 12, maxWidth: 240 }}
            >
              {inspector.email}
            </div>
          </div>
        </div>
      </CTableDataCell>

      <CTableDataCell style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        {inspector.phone || <Dash />}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {inspector.inspectorForPlace?.name ? (
          <Chip>{inspector.inspectorForPlace.name}</Chip>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex justify-content-end">
          <IconAction
            title="Supprimer"
            danger
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

interface TableProps {
  Inspectors: Inspector[];
  InspectorsTableToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function InspectorsTable({
  Inspectors,
  InspectorsTableToBeDeleted,
}: TableProps) {
  const { token } = useCookie("vToken");
  const { toastSuccess, toastError } = useToast();
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<Inspector | null>(null);

  const deleteMutation = useMutation((id: string) =>
    apiService.MakeDeleteRequest(`inspectors/${id}`, token),
  );

  const confirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess(general.fr.message.inspectorDeleted);
        // The page list uses "getInspectors"
        queryClient.invalidateQueries("getInspectors");
        queryClient.invalidateQueries("getAllInspector");
      },
    });
    setToDelete(null);
  };

  const deletingId = deleteMutation.isLoading
    ? deleteMutation.variables
    : undefined;

  return (
    <div>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={thStyle}>Inspecteur</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Téléphone</CTableHeaderCell>
            <CTableHeaderCell style={thStyle}>Établissement</CTableHeaderCell>
            <CTableHeaderCell style={{ ...thStyle, textAlign: "right" }}>
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {Inspectors?.map((inspector) => (
            <InspectorRow
              key={inspector.id}
              inspector={inspector}
              deletedList={InspectorsTableToBeDeleted}
              isDeleting={deletingId === inspector.id}
              onDelete={() => setToDelete(inspector)}
            />
          ))}
        </CTableBody>
      </CTable>

      <PromptModal
        open={toDelete !== null}
        onAccept={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}
